/**
 * OT and shift metrics from raw punch docs (same 9h paid threshold as Daily hours).
 */
import {
  collectPunchTimes,
  mergeAndDedupeTimestamps,
  computeSessionsFromTimestamps,
  attributedMsInDay,
  otFromAttributedMs,
  shortOfPaidDayThresholdMs,
} from "./staffDailyHours";
import { parseClockToMinutes } from "./dailyHoursPayroll";
import {
  fetchAttendanceForMonth,
  ymd,
  toDateSafe,
  extractDayKey,
  firstDefined,
  extractEmpKeyWithCutover,
} from "./payrollAttendanceCore";

const DEDUPE_SEC = 10;

function dayBoundsFromYmdKey(dayKey) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const da = Number(m[3]);
  const startOfDay = new Date(y, mo, da, 0, 0, 0, 0);
  const endOfDay = new Date(y, mo, da + 1, 0, 0, 0, 0);
  return { startOfDay, endOfDay };
}

function punchDayKeyFromDoc(d, docId, monthStart, monthEnd) {
  const explicitDayKey = extractDayKey(d);
  let dayKey = explicitDayKey;
  let dateObj = null;
  if (explicitDayKey) {
    dateObj = toDateSafe(explicitDayKey);
    if (!dateObj && /^\d{4}-\d{2}-\d{2}$/.test(explicitDayKey)) {
      const [y, mo, day] = explicitDayKey.split("-").map(Number);
      dateObj = new Date(y, mo - 1, day);
    }
  } else {
    const rawDate = firstDefined(d, [
      "timestamp",
      "punchTime",
      "punchDateTime",
      "date",
      "Date",
      "attendanceDate",
      "createdAt",
      "inTime",
      "checkInTime",
      "checkIn",
      "timeStamp",
    ]);
    dateObj = toDateSafe(rawDate);
    if (dateObj) dayKey = ymd(dateObj);
  }
  if (!dateObj || !dayKey || dateObj < monthStart || dateObj >= monthEnd) return null;
  return { dayKey, dateObj };
}

/**
 * @returns {Promise<{ byEmployee: Record<string, { employeeId: string, otMs: number, daysWithOt: number, lackMs: number, byDay: { dayKey: string, attributedMs: number, otMs: number, lackMs: number }[] }>, mode: string }>}
 */
export async function aggregateOtByEmployeeForMonth(punchCollectionName, monthStart, monthEnd, employeeCodeLookup) {
  const attPack = await fetchAttendanceForMonth(punchCollectionName, monthStart, monthEnd);
  /** key `${empId}||${dayKey}` -> docs data[] */
  const byEmpDay = new Map();
  for (const docSnap of attPack.snap.docs) {
    const d = docSnap.data() || {};
    const pd = punchDayKeyFromDoc(d, docSnap.id, monthStart, monthEnd);
    if (!pd) continue;
    const empId = extractEmpKeyWithCutover(d, docSnap.id, pd.dateObj, employeeCodeLookup);
    if (!empId) continue;
    const k = `${empId}||${pd.dayKey}`;
    if (!byEmpDay.has(k)) byEmpDay.set(k, []);
    byEmpDay.get(k).push(d);
  }

  /** @type {Record<string, { employeeId: string, otMs: number, daysWithOt: number, lackMs: number, byDay: { dayKey: string, attributedMs: number, otMs: number, lackMs: number }[] }>} */
  const byEmployee = {};
  for (const [k, docs] of byEmpDay) {
    const sep = k.indexOf("||");
    const empId = sep >= 0 ? k.slice(0, sep) : k;
    const dayKey = sep >= 0 ? k.slice(sep + 2) : "";
    const bounds = dayBoundsFromYmdKey(dayKey);
    if (!bounds) continue;
    const times = [];
    for (const data of docs) {
      times.push(...collectPunchTimes(data));
    }
    if (times.length === 0) continue;
    const merged = mergeAndDedupeTimestamps(times, DEDUPE_SEC);
    const { sessions } = computeSessionsFromTimestamps(merged);
    const attributedMs = attributedMsInDay(sessions, bounds.startOfDay, bounds.endOfDay);
    const otMs = otFromAttributedMs(attributedMs);
    const lackMs = shortOfPaidDayThresholdMs(attributedMs);
    if (!byEmployee[empId]) {
      byEmployee[empId] = { employeeId: empId, otMs: 0, daysWithOt: 0, lackMs: 0, byDay: [] };
    }
    byEmployee[empId].otMs += otMs;
    byEmployee[empId].lackMs += lackMs;
    if (otMs > 0) byEmployee[empId].daysWithOt += 1;
    byEmployee[empId].byDay.push({ dayKey, attributedMs, otMs, lackMs });
  }
  for (const e of Object.values(byEmployee)) {
    e.byDay.sort((a, b) => a.dayKey.localeCompare(b.dayKey));
  }
  return { byEmployee, mode: attPack.mode };
}

/**
 * @param {Record<string, string>} expectedStartByEmpId — canonical employee id -> "HH:mm"
 * @returns {Promise<{ rows: { employeeId: string, lateDays: number, totalLateMin: number }[], mode: string }>}
 */
export async function aggregateLateFirstPunchForMonth(
  punchCollectionName,
  monthStart,
  monthEnd,
  expectedStartByEmpId,
  employeeCodeLookup
) {
  const attPack = await fetchAttendanceForMonth(punchCollectionName, monthStart, monthEnd);
  /** `${empId}||${dayKey}` -> Date[] punch instants */
  const timesByEmpDay = new Map();
  for (const docSnap of attPack.snap.docs) {
    const d = docSnap.data() || {};
    const pd = punchDayKeyFromDoc(d, docSnap.id, monthStart, monthEnd);
    if (!pd) continue;
    const empId = extractEmpKeyWithCutover(d, docSnap.id, pd.dateObj, employeeCodeLookup);
    if (!empId) continue;
    const exp = expectedStartByEmpId[empId];
    if (!exp || !parseClockToMinutes(exp)) continue;
    const times = collectPunchTimes(d);
    if (times.length === 0) continue;
    const k = `${empId}||${pd.dayKey}`;
    if (!timesByEmpDay.has(k)) timesByEmpDay.set(k, []);
    timesByEmpDay.get(k).push(...times);
  }

  const rowsMap = {};
  for (const [k, times] of timesByEmpDay) {
    const [empId] = k.split("||");
    const expStr = expectedStartByEmpId[empId];
    const expMin = parseClockToMinutes(expStr);
    if (expMin == null) continue;
    const merged = mergeAndDedupeTimestamps(times, DEDUPE_SEC);
    if (merged.length === 0) continue;
    const first = merged[0];
    const dayMin = first.getHours() * 60 + first.getMinutes();
    let late = dayMin - expMin;
    if (late < 0) late = 0;
    if (!rowsMap[empId]) rowsMap[empId] = { employeeId: empId, lateDays: 0, totalLateMin: 0 };
    if (late > 0) {
      rowsMap[empId].lateDays += 1;
      rowsMap[empId].totalLateMin += late;
    }
  }
  const rows = Object.values(rowsMap).sort((a, b) => a.employeeId.localeCompare(b.employeeId));
  return { rows, mode: attPack.mode };
}
